import { MealTime } from "@/clients/models/MealTime";
import { GO_TO_PLAN_ALL_CANDIDATES_GENERATION_RETRY_INTERVAL } from "@/constants/plan";
import { QueryKeys } from "@/constants/query-keys";
import { useGetGotoGenerationStatus } from "@/hooks/requests/go-to/use-get-generation-status";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";

interface UseCheckCandidateCreationProps {
  mealTime: MealTime;
  onCompleted: () => void;
}

export function useCheckCandidateCreation({
  mealTime,
  onCompleted,
}: UseCheckCandidateCreationProps) {
  const counterRef = useRef(0);
  const timerRef = useRef<NodeJS.Timeout>();
  const queryClient = useQueryClient();
  const { isCompleted, refetch } = useGetGotoGenerationStatus(mealTime);

  useEffect(() => {
    startUpdateTimer();
    return () => {
      stopUpdateTimer();
    };
  }, []);

  useEffect(() => {
    if (isCompleted) {
      stopUpdateTimer();
      queryClient.removeQueries({
        predicate: (query) =>
          query.queryKey.includes(QueryKeys.GO_TO_CANDIDATE) ||
          query.queryKey.includes(QueryKeys.GO_TO_SELECT_MEAL),
      });
      onCompleted();
    }
  }, [isCompleted]);

  const startUpdateTimer = useCallback(() => {
    timerRef.current = setInterval(() => {
      if (
        counterRef.current > GO_TO_PLAN_ALL_CANDIDATES_GENERATION_RETRY_INTERVAL
      ) {
        stopUpdateTimer();
        return;
      }
      counterRef.current++;
      refetch();
    }, 2000);
  }, [refetch]);

  const stopUpdateTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  }, []);
}
